import { IsGreaterThan, SumEquals } from '@dermloop/utils';
import { Exclude, Type } from 'class-transformer';
import {
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateNested,
} from 'class-validator';
import { BaseCreateDto, BaseUpdateDto } from '../base.dto';
import { NamedEntityDto } from '../clinical-details';

export interface TrainingConditionDifficultyLevelDto extends NamedEntityDto {
  minDifficulty: number;

  maxDifficulty: number;

  progressionAccuracy?: number;

  downgradeAccuracy?: number;

  mixinCurrentDifficulty: number;

  mixinPreviousDifficulties: number;

  mixinNoDifficulty: number;
  difficultyLevelGroupingId: string;
  notes?: string;
}

/**
 * DTO for batch creating and updating condition difficulty levels.
 */
export class BatchTrainingConditionDifficultyLevelDto extends BaseCreateDto {
  @IsUUID()
  difficultyLevelGroupingId: string;

  @IsOptional()
  @Type(() => CreateTrainingConditionDifficultyLevelDto)
  @ValidateNested({ each: true })
  createDifficultyLevels?: CreateTrainingConditionDifficultyLevelDto[];

  @IsOptional()
  @Type(() => UpdateTrainingConditionDifficultyLevelDto)
  @ValidateNested({ each: true })
  updateDifficultyLevels?: UpdateTrainingConditionDifficultyLevelDto[];
}

export class CreateTrainingConditionDifficultyLevelDto
  extends BaseCreateDto
  implements Partial<TrainingConditionDifficultyLevelDto>
{
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsOptional()
  @IsString()
  name_da?: string;

  @IsOptional()
  @IsString()
  name_de?: string;

  @IsNumber()
  @Min(0)
  @Max(1)
  minDifficulty: number;

  @IsNumber()
  @Min(0)
  @Max(1)
  @IsGreaterThan<CreateTrainingConditionDifficultyLevelDto>('minDifficulty', {
    message: 'maxDifficulty must be greater than minDifficulty',
  })
  maxDifficulty: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  progressionAccuracy: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  downgradeAccuracy?: number;

  @IsNumber()
  @Min(0)
  @Max(1)
  mixinCurrentDifficulty: number;

  @IsNumber()
  @Min(0)
  @Max(1)
  mixinPreviousDifficulties: number;

  @IsNumber()
  @Min(0)
  @Max(1)
  @SumEquals<CreateTrainingConditionDifficultyLevelDto>(
    1,
    ['mixinCurrentDifficulty', 'mixinPreviousDifficulties'],
    { message: 'All mixin difficulties must sum to 1' },
  )
  mixinNoDifficulty: number;

  @Exclude()
  difficultyLevelGrouping: never;

  @IsUUID()
  difficultyLevelGroupingId: string;

  @IsOptional()
  @IsString()
  notes?: string;
}

export class UpdateTrainingConditionDifficultyLevelDto
  extends BaseUpdateDto
  implements Partial<TrainingConditionDifficultyLevelDto>
{
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  name?: string;

  @IsOptional()
  @IsString()
  name_da?: string;

  @IsOptional()
  @IsString()
  name_de?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  minDifficulty?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  @IsGreaterThan<CreateTrainingConditionDifficultyLevelDto>('minDifficulty')
  maxDifficulty?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  progressionAccuracy?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  downgradeAccuracy?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  mixinCurrentDifficulty?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  mixinPreviousDifficulties?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  @SumEquals<CreateTrainingConditionDifficultyLevelDto>(1, [
    'mixinCurrentDifficulty',
    'mixinPreviousDifficulties',
  ])
  mixinNoDifficulty?: number;

  @Exclude()
  difficultyLevelGrouping?: never;

  @IsOptional()
  @IsString()
  difficultyLevelGroupingId?: string;

  @IsOptional()
  @IsString()
  notes?: string;
}

/**
 * DTO for deleting a training condition difficulty level. A new difficulty level id
 * must be provided, that existing users are moved to before delete.
 */
export class DeleteTrainingConditionDifficultyLevelQueryDto {
  @IsUUID()
  moveUsersToTrainingConditionDifficultyLevelId: string;
}
